<script setup>
import { ref, defineProps, defineEmits, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { countryPrefixes } from '@/utils/prefix.js';
import KiwishModal from '@/views/components/KiwishModal.vue';

const { t } = useI18n();
const isNative = window.Capacitor?.isNativePlatform?.() ?? false;

// Importación dinámica de Contacts solo en nativa
let Contacts = null;
let contactsLoaded = false;
const loadContacts = async () => {
  if (!isNative || contactsLoaded) return;
  try {
    const module = await import('@capacitor-community/contacts');
    Contacts = module.Contacts;
    contactsLoaded = true;
  } catch (e) {
    console.error('Error loading Contacts module:', e);
  }
};

// Cargar el módulo inmediatamente si es nativa
if (isNative) {
  loadContacts();
}
const props = defineProps({
  prefix: String,
  phone: String,
  disabled: {
    type: Boolean,
    default: false
  },
  showContactPicker: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:prefix', 'update:phone', 'update:isValidPhone', 'enter']);

// Función para formatear el teléfono (definida antes de usarla)
const formatPhone = (value) => {
  if (!value) return '';
  if (value.length <= 3) return value;
  if (value.length <= 6) return `${value.slice(0,3)} ${value.slice(3)}`;
  return `${value.slice(0,3)} ${value.slice(3,6)} ${value.slice(6)}`;
};

// Inicializar con formato
const phone = ref(formatPhone(props.phone?.replace(/\D/g, '') || ''));
const prefix = ref(props.prefix || '');

// Flag para saber si el usuario ya interactuó con el prefijo (evita que geoIP sobrescriba)
const userHasSelectedPrefix = ref(false);

// Watchers para sincronizar props con refs locales
watch(() => props.phone, (newVal) => {
  if (newVal !== undefined) {
    // Comparar solo los dígitos, ya que phone.value está formateado con espacios
    const currentDigits = phone.value.replace(/\D/g, '');
    const newDigits = (newVal || '').replace(/\D/g, '');
    if (newDigits !== currentDigits) {
      phone.value = formatPhone(newDigits);
    }
  }
});

watch(() => props.prefix, (newVal) => {
  if (newVal !== undefined && newVal !== prefix.value) {
    prefix.value = newVal;
  }
});

// Estado para el selector de teléfonos múltiples
const showPhonePicker = ref(false);
const availablePhones = ref([]);
const isPickingContact = ref(false); // Prevenir clics múltiples

// Estado para selector de contactos (fallback cuando pickContact falla)
const showContactList = ref(false);
const availableContacts = ref([]);

// Emitir cambios cuando cambian los valores
const updatePrefix = (value) => {
  prefix.value = value;
  emit('update:prefix', value);
};

// Handler para cuando el usuario selecciona manualmente un prefijo
const onPrefixChange = (event) => {
  const newValue = event.detail.value;
  if (newValue) {
    userHasSelectedPrefix.value = true; // Marcar que el usuario seleccionó manualmente
    prefix.value = newValue;
    emit('update:prefix', newValue);
  }
};
const isValidPhone = (value) => {
  const n = value.replace(/\D/g, '');
  return n.length >= 9 && n.length <= 15;
};

const extractPrefix = (raw) => {
  // Ordenamos por longitud descendente para evitar coger "+3" antes que "+34" o "+351"
  const sorted = [...countryPrefixes].sort(
    (a, b) => b.dialCode.length - a.dialCode.length
  );

  for (const item of sorted) {
    const pref = item.dialCode; // "+34", "+351", etc.

    if (raw.startsWith(pref)) {
      return {
        prefix: pref,
        phone: raw.slice(pref.length)
      };
    }
  }

  return null;
};

const updatePhone = (event) => {
  let raw = event.target.value.replace(/\s+/g, '').replace(/-/g, '');

  // 1) Caso: viene con "+"
  if (raw.startsWith('+')) {
    const extracted = extractPrefix(raw);
    if (extracted) {
      updatePrefix(extracted.prefix);

      const digits = extracted.phone.replace(/\D/g, '').slice(0, 15);
      phone.value = formatPhone(digits);

      emit('update:phone', digits);
      emit('update:isValidPhone', isValidPhone(digits));
      return;
    }
  }

  // 2) Caso: viene con "00"
  if (raw.startsWith('00')) {
    raw = '+' + raw.slice(2); // 0034 → +34
    const extracted = extractPrefix(raw);
    if (extracted) {
      updatePrefix(extracted.prefix);

      const digits = extracted.phone.replace(/\D/g, '').slice(0, 15);
      phone.value = formatPhone(digits);

      emit('update:phone', digits);
      emit('update:isValidPhone', isValidPhone(digits));
      return;
    }
  }

  // 3) Sin prefijo: sólo teléfono
  const digits = raw.replace(/\D/g, '').slice(0, 15);

  phone.value = formatPhone(digits);
  emit('update:phone', digits);
  emit('update:isValidPhone', isValidPhone(digits));
};

const customModalOptions = {
  header: t('country'),
  breakpoints: [0, 0.8],
  initialBreakpoint: 0.8,
  buttons: [
    {
      text: '',
      icon: 'close',
      role: 'cancel'
    }
  ]
};

// Prefijo por defecto (España)
const DEFAULT_PREFIX = '+34';

const getDefaultPrefix = () => {
  return countryPrefixes.find((item) => item.code === 'ES')?.dialCode || DEFAULT_PREFIX;
};

onMounted(async () => {
  if (!props.prefix) {
    await geoIpLookup(
      (countryCode) => {
        // Solo aplicar geoIP si el usuario NO ha seleccionado un prefijo manualmente
        if (!userHasSelectedPrefix.value && !prefix.value) {
          const foundPrefix = countryPrefixes.find((item) => item.code === countryCode)?.dialCode;
          updatePrefix(foundPrefix || getDefaultPrefix());
        }
      },
      () => {
        // Solo aplicar default si el usuario NO ha seleccionado un prefijo manualmente
        if (!userHasSelectedPrefix.value && !prefix.value) {
          updatePrefix(getDefaultPrefix());
        }
      }
    );
  }
});

const geoIpLookup = (success, failure) => {
  try {
    fetch("https://ipapi.co/json", {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    })
      .then((res) => {
        if (!res.ok) throw new Error('Network error');
        return res.json();
      })
      .then((data) => {
        if (data?.country_code) {
          success(data.country_code);
        } else {
          failure();
        }
      })
      .catch(() => failure());
  } catch (error) {
    // Error en fetch, usar fallback
    failure();
  }
};

const selectPhoneNumber = (phoneNumber) => {
  // Limpiar el número de espacios, guiones, paréntesis
  phoneNumber = phoneNumber.replace(/[\s\-()]/g, '');

  // Simular un evento input para procesar el número
  const fakeEvent = { target: { value: phoneNumber } };
  updatePhone(fakeEvent);

  showPhonePicker.value = false;
  availablePhones.value = [];
};

const phonePickerButtons = () => {
  const buttons = availablePhones.value.map(p => ({
    text: `${p.label ? p.label + ': ' : ''}${p.number}`,
    handler: () => selectPhoneNumber(p.number)
  }));

  buttons.push({
    text: t('cancel'),
    role: 'cancel',
    handler: () => {
      showPhonePicker.value = false;
      availablePhones.value = [];
    }
  });

  return buttons;
};

// Seleccionar un contacto de nuestra lista custom
const selectContact = (contact) => {
  showContactList.value = false;

  if (contact.phones?.length > 0) {
    if (contact.phones.length === 1) {
      selectPhoneNumber(contact.phones[0].number);
    } else {
      // Múltiples teléfonos - mostrar selector
      availablePhones.value = contact.phones;
      showPhonePicker.value = true;
    }
  }
  availableContacts.value = [];
};

// Fallback: obtener contactos y mostrar nuestro propio selector
const fallbackGetContacts = async () => {
  try {
    const result = await Contacts.getContacts({
      projection: {
        name: true,
        phones: true,
      }
    });

    if (result?.contacts?.length > 0) {
      // Filtrar solo contactos con teléfono
      const contactsWithPhones = result.contacts.filter(c => c.phones?.length > 0);

      if (contactsWithPhones.length > 0) {
        availableContacts.value = contactsWithPhones;
        showContactList.value = true;
      }
    }
  } catch (fallbackErr) {
    // No hay acceso a contactos
  }
};

// Estado para mostrar alerta de permisos
const showPermissionModal = ref(false);

// Abrir ajustes de la app
const openAppSettings = async () => {
  showPermissionModal.value = false;
  try {
    // Capacitor App plugin puede abrir los ajustes
    const { App } = await import('@capacitor/app');
    // En iOS, app-settings: abre los ajustes de la app
    window.location.href = 'app-settings:';
  } catch {
    // Fallback
    window.location.href = 'app-settings:';
  }
};

const pickContact = async () => {
  // Prevenir clics múltiples
  if (isPickingContact.value) return;

  // Asegurar que el módulo está cargado
  if (!Contacts) {
    await loadContacts();
  }

  if (!isNative || !Contacts) return;

  isPickingContact.value = true;

  try {
    // Intentar usar pickContact nativo
    const result = await Contacts.pickContact({
      projection: {
        phones: true,
      }
    });

    if (result?.contact?.phones?.length > 0) {
      const phones = result.contact.phones;

      if (phones.length === 1) {
        selectPhoneNumber(phones[0].number);
      } else {
        availablePhones.value = phones;
        showPhonePicker.value = true;
      }
    }
  } catch (err) {
    // Si falla por permisos, mostrar modal para ir a Ajustes
    const errorMsg = err?.message || err?.errorMessage || '';
    if (errorMsg.includes('Permission')) {
      showPermissionModal.value = true;
    }
  } finally {
    setTimeout(() => {
      isPickingContact.value = false;
    }, 500);
  }
};
</script>

<template>
  <div>
    <ion-item lines="full" role="group" :aria-label="$t('phone')">
      <ion-select
        :aria-label="$t('country')"
        interface="modal"
        :placeholder="$t('country')"
        :value="prefix"
        :interface-options="customModalOptions"
        :selectedText="prefix"
        class="phone-input"
        :readonly="disabled"
        @ionChange="onPrefixChange"
      >
        <ion-select-option v-for="item in countryPrefixes" :key="item.dialCode" :value="item.dialCode">
          {{ item.dialCode }} - {{ item.name }}
        </ion-select-option>
      </ion-select>

      <ion-input
        type="tel"
        placeholder="600 000 000"
        v-model="phone"
        class="phone-input"
        @input="updatePhone"
        @keyup.enter="emit('enter')"
        :readonly="disabled"
        :aria-label="$t('phone')"
      />

      <ion-button
        v-if="isNative && !disabled && showContactPicker"
        fill="clear"
        color="primary"
        @click="pickContact"
        class="contact-button"
        :class="{ 'contact-button-loading': isPickingContact }"
        :disabled="isPickingContact"
        :aria-label="$t('phone.select')"
      >
        <ion-icon v-if="!isPickingContact" slot="icon-only" icon="person-circle-outline" aria-hidden="true" />
        <ion-spinner v-else name="crescent" slot="icon-only" />
      </ion-button>
    </ion-item>

    <ion-action-sheet
      :is-open="showPhonePicker"
      :header="$t('phone.select')"
      :buttons="phonePickerButtons()"
      @didDismiss="showPhonePicker = false"
    />

    <!-- Modal para seleccionar contacto (fallback iOS limited access) -->
    <ion-modal :is-open="showContactList" @didDismiss="showContactList = false" class="contact-list-modal">
      <ion-header>
        <ion-toolbar>
          <ion-title>{{ $t('phone.select.contact') || 'Seleccionar contacto' }}</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="showContactList = false">
              <ion-icon name="close"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content>
        <ion-list>
          <ion-item
            v-for="contact in availableContacts"
            :key="contact.contactId"
            button
            @click="selectContact(contact)"
          >
            <ion-label>
              <h2>{{ contact.name?.display || contact.name?.given || 'Sin nombre' }}</h2>
              <p v-if="contact.phones?.length">{{ contact.phones[0].number }}</p>
            </ion-label>
          </ion-item>
        </ion-list>
      </ion-content>
    </ion-modal>

    <!-- Modal para permisos de contactos -->
    <KiwishModal
      :is-open="showPermissionModal"
      max-width="350px"
      @close="showPermissionModal = false"
    >
      <div class="permission-modal-inner">
        <div class="ion-text-center">
          <ion-icon name="person-circle-outline" class="permission-icon"></ion-icon>
        </div>
        <div class="ion-text-center mt-4">
          <ion-text class="permission-title">{{ $t('phone.permission.title') }}</ion-text>
        </div>
        <div class="ion-text-center mt-3">
          <ion-text class="permission-message">{{ $t('phone.permission.message') }}</ion-text>
        </div>
        <div class="permission-buttons">
          <ion-button class="button-outline-pink" expand="block" @click="showPermissionModal = false">
            {{ $t('cancel') }}
          </ion-button>
          <ion-button class="button-pink" expand="block" @click="openAppSettings">
            {{ $t('phone.permission.settings') }}
          </ion-button>
        </div>
      </div>
    </KiwishModal>
  </div>
</template>

<style>
ion-input.phone-input {
  font-size: 1.2rem !important;
  label {
    --color: #5A695B !important;
    color: #5A695B !important;
    opacity: 1 !important;
  }
}
ion-item {
  --background: transparent !important;
  background: transparent !important;
}

ion-select {
  --placeholder-color: #FF5287;
  --placeholder-opacity: 1;
  --background: transparent !important;
  --ion-background-color: transparent !important;
  background: transparent !important;
}
ion-select::part(text) {
  color: #5A695B !important;
  --color: #5A695B !important;
}

ion-input {
  margin-left: 10px;
  --background: transparent !important;
  --ion-background-color: transparent !important;
  background: transparent !important;
}
ion-item::part(native) {
  background: transparent !important;
}

ion-select::part(native) {
  background: transparent !important;
}

ion-input::part(native) {
  background: transparent !important;
  --color: #5A695B !important;
  color: #5A695B !important;
}

ion-select-modal {
  --background: #fff !important;
  background: #fff !important;
}

ion-select-modal ion-content {
  --background-color: #fff !important;
  background-color: #fff !important;
}
ion-list ion-item {
  --background: #fff !important;
  background: #fff !important;
  --color: #5A695B !important;
  color: #5A695B !important;
}

ion-item {
  --border-width: 0 !important;
  --inner-border-width: 0 !important;
}


ion-radio.sc-ion-select-modal-ios::after
{
  border: 0px;
}

ion-list {
  overflow-y: auto !important;
  max-height: 65vh !important;
}

ion-toolbar {
  --background: #fff !important;
  background: #fff !important;
  --color: #404B52 !important;
  color: #404B52 !important;
}

.contact-button {
  --color: #FF5287 !important;
  color: #FF5287 !important;
  --padding-start: 4px;
  --padding-end: 0;
  margin: 0;
  min-height: auto;
  height: auto;
}

.contact-button ion-icon {
  font-size: 1.8rem;
  color: #FF5287 !important;
}

.contact-button ion-spinner {
  --color: #FF5287;
  width: 1.5rem;
  height: 1.5rem;
}

.contact-button-loading {
  opacity: 0.7;
}

/* Estilos del Action Sheet para selector de teléfonos */
ion-action-sheet {
  --background: #fff7e2;
  --button-background: #fff7e2;
  --button-background-selected: #ffe9c7;
  --button-background-activated: #ffe9c7;
  --button-color: #5A695B;
  --color: #5A695B;
}

ion-action-sheet .action-sheet-title {
  color: #FF5287 !important;
  font-weight: bold;
}

ion-action-sheet .action-sheet-button {
  color: #5A695B !important;
}

ion-action-sheet .action-sheet-cancel {
  color: #FF5287 !important;
  font-weight: bold;
}

/* Estilos del modal de lista de contactos */
.contact-list-modal {
  --background: #fff7e2;
}

.contact-list-modal ion-toolbar {
  --background: #fff7e2;
  --color: #5A695B;
}

.contact-list-modal ion-title {
  color: #FF5287;
  font-weight: bold;
}

.contact-list-modal ion-content {
  --background: #fff7e2;
}

.contact-list-modal ion-list {
  background: transparent;
}

.contact-list-modal ion-item {
  --background: #fff;
  --color: #5A695B;
  margin: 8px 16px;
  border-radius: 12px;
}

.contact-list-modal ion-item h2 {
  color: #5A695B;
  font-weight: 600;
}

.contact-list-modal ion-item p {
  color: #888;
}

.contact-list-modal ion-button ion-icon {
  color: #5A695B;
  font-size: 1.5rem;
}

/* Estilos del modal de permisos */
.permission-modal-inner {
  text-align: center;
  padding: 10px 0;
}

.permission-icon {
  font-size: 4rem;
  color: #FF5287;
}

.permission-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #5A695B;
}

.permission-message {
  font-size: 1rem;
  color: #5A695B;
  line-height: 1.5;
}

.permission-buttons {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  margin-top: 1.5rem;
  gap: 10px;
}

.permission-buttons ion-button {
  -webkit-box-flex: 1;
  -webkit-flex: 1;
  flex: 1;
}

.button-pink {
  --background: #FF5287 !important;
  --background-activated: #e94a78 !important;
  --background-hover: #ff6b9a !important;
  --color: #FFFFFF !important;
}

.button-outline-pink {
  --background: #fff;
  --background-activated: rgba(255, 82, 135, 0.1) !important;
  --background-hover: rgba(255, 82, 135, 0.05) !important;
  --border-color: #FF5287 !important;
  --border-width: 2px !important;
  --border-style: solid !important;
  --color: #FF5287 !important;
}
</style>